import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const STATUS = [
  { k: "System Status", v: "Backend Infrastructure in Development" },
  { k: "Environment", v: "Controlled / Non-Public" },
  { k: "Access Layer", v: "Restricted" },
  { k: "External Availability", v: "Pending Operational Clearance" },
];

const BODY = [
  "Triarch is currently operating in a controlled development environment.",
  "The public interface is visible only as an entry layer. The intelligence backend, vector-processing systems, data-structuring pipelines, access-control framework, and internal orchestration layer are still being built, tested, and validated before external users are permitted into the system.",
  "At this stage, access is not automatically granted. Each request is recorded for future review and may be evaluated based on operational readiness, use-case relevance, compliance requirements, and system capacity.",
  "Your request has been received and placed into the restricted access queue.",
];

/**
 * Global restricted access-status modal.
 * Opens when any "Request Access" button dispatches the
 * "triarch:request-access" window event (see grantAccess).
 */
export const AccessModal = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("triarch:request-access", onOpen);
    return () => window.removeEventListener("triarch:request-access", onOpen);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const close = () => setOpen(false);

  const reveal = {
    hidden: { opacity: 0, y: 8 },
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, delay: 0.28 + i * 0.07, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="access-modal"
          className="fixed inset-0 z-[80] flex items-stretch justify-center sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/78 backdrop-blur-md"
            onClick={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* panel */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Access request under review"
            initial={{ opacity: 0, y: 30, filter: "blur(16px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: 20, filter: "blur(12px)" }}
            transition={{ duration: 0.62, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 flex h-full w-full max-w-2xl flex-col overflow-hidden border border-white/10 bg-[#0A0C0F]/92 shadow-[0_40px_130px_rgba(0,0,0,0.75)] backdrop-blur-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[1.4rem]"
          >
            {/* faint grain */}
            <div className="triarch-noise pointer-events-none absolute inset-0 opacity-[0.06]" />
            {/* blurred oversized logo */}
            <img
              src="/triarch-logo.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -right-28 -top-28 h-[460px] w-[460px] opacity-[0.05] blur-[2px]"
            />
            {/* very faint grid */}
            <div className="pointer-events-none absolute inset-0 triarch-grid opacity-20" />
            {/* radial gold glow */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-48"
              style={{
                background:
                  "radial-gradient(620px 180px at 26% 0%, rgba(203,174,120,0.1), transparent 70%)",
              }}
            />
            {/* edge pulse */}
            <motion.div
              className="pointer-events-none absolute inset-0 z-[1] sm:rounded-[1.4rem]"
              style={{ border: "1px solid rgba(201,174,120,0.06)" }}
              animate={{ opacity: [0.3, 0.85, 0.3] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
            {/* ambient system fragments */}
            <motion.div
              className="pointer-events-none absolute left-6 top-1/2 hidden -translate-y-1/2 flex-col gap-3 font-mono-accent text-[9px] uppercase tracking-[0.25em] text-white/[0.12] sm:flex"
              animate={{ opacity: [0.4, 0.75, 0.4] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            >
              <span>Q-01</span>
              <span>Q-02</span>
              <span>Q-03</span>
            </motion.div>

            {/* header */}
            <div className="relative border-b border-white/[0.08] px-6 py-5 sm:px-8 sm:py-6">
              <motion.span
                variants={reveal}
                custom={0}
                initial="hidden"
                animate="show"
                className="font-mono-accent text-[10px] uppercase tracking-[0.32em] text-[#C9AE78]/85"
              >
                Access Layer // Restricted
              </motion.span>
            </div>

            {/* body */}
            <div className="relative min-h-0 flex-1 overflow-y-auto px-6 py-7 sm:px-8 sm:py-8">
              <motion.h3
                variants={reveal}
                custom={1}
                initial="hidden"
                animate="show"
                data-testid="access-modal-headline"
                className="font-display text-xl font-semibold uppercase leading-[1.15] tracking-[0.14em] text-white text-glow sm:text-2xl"
              >
                Access Request Under Review
              </motion.h3>

              <div className="mt-6 space-y-4">
                {BODY.map((p, i) => (
                  <motion.p
                    key={`b-${i}`}
                    variants={reveal}
                    custom={2 + i}
                    initial="hidden"
                    animate="show"
                    className={
                      i === 3
                        ? "border-l border-[#C9AE78]/40 pl-4 text-sm leading-relaxed text-white/80"
                        : "text-sm leading-relaxed text-white/60"
                    }
                  >
                    {p}
                  </motion.p>
                ))}
              </div>

              <motion.div
                variants={reveal}
                custom={6}
                initial="hidden"
                animate="show"
                className="mt-7"
              >
                <div className="mb-3 h-px w-full bg-gradient-to-r from-white/12 to-transparent" />
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-4 sm:p-5">
                  <div className="space-y-2.5">
                    {STATUS.map((s) => (
                      <div
                        key={s.k}
                        className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                      >
                        <div className="flex items-center gap-2">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#C4A061]/80 shadow-[0_0_10px_rgba(196,160,97,0.5)]" />
                          <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/40">
                            {s.k}
                          </span>
                        </div>
                        <span className="pl-3.5 font-mono-accent text-[11px] uppercase tracking-[0.12em] text-white/75 sm:pl-0 sm:text-right">
                          {s.v}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* footer */}
            <div className="relative flex items-center justify-between gap-4 border-t border-white/[0.08] px-6 py-4 sm:px-8">
              <span className="hidden font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/30 sm:inline">
                Request Logged
              </span>
              <button
                onClick={close}
                data-testid="access-modal-acknowledge"
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#C9AE78]/30 bg-white/[0.04] px-6 py-3 font-mono-accent text-xs uppercase tracking-[0.22em] text-white/85 transition-all duration-300 hover:border-[#C9AE78]/55 hover:bg-white/[0.07] hover:text-white hover:shadow-[0_0_30px_rgba(203,174,120,0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9AE78]/40 sm:w-auto"
              >
                Acknowledge
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
